import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Menu } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "./ui/sheet";

export const logoUrl = "https://customer-assets.emergentagent.com/job_bim-solutions/artifacts/m8dxb8rv_Final%20logo%20lone%20jpeg.jpg";

const navItems = [
  { label: "Accueil", href: "/", kind: "route", testId: "marketing-nav-home-link" },
  { label: "BIM 3D", href: "/bim-3d-synthese", kind: "route", testId: "marketing-nav-bim3d-link" },
  { label: "BIM 4D", href: "/bim-4d-planning", kind: "route", testId: "marketing-nav-bim4d-link" },
  { label: "Formations", href: "/formations", kind: "route", testId: "marketing-nav-training-link" },
  { label: "Logiciels", href: "/logiciels", kind: "route", testId: "marketing-nav-software-link" },
];

const internalAccessItems = [
  { label: "Management", href: "/app/management", testId: "marketing-nav-management-link" },
  { label: "Admin", href: "/admin", testId: "marketing-nav-admin-link" },
];

const NavItem = ({ item, onNavigate, mobile = false }) => {
  const className = mobile
    ? "rounded-full border border-[#d8ece4] px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-[#99f6e4] hover:bg-[#ecfdf5] hover:text-[#0f766e]"
    : "text-[15px] font-medium text-slate-700 transition hover:text-[#0f766e]";

  if (item.kind === "route") {
    return (
      <Link className={className} data-testid={item.testId} onClick={onNavigate} to={item.href}>
        {item.label}
      </Link>
    );
  }

  return (
    <a className={className} data-testid={item.testId} href={item.href} onClick={onNavigate}>
      {item.label}
    </a>
  );
};

export const MarketingHeader = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-[#d8ece4]/90 bg-[rgba(252,254,249,0.88)] backdrop-blur-xl" data-testid="marketing-header">
      <div className="mx-auto flex h-[5.5rem] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link className="flex items-center gap-3.5" data-testid="marketing-header-logo-link" to="/">
          <div className="shrink-0 rounded-2xl border border-[#d8ece4] bg-white/92 p-2.5 shadow-[0_16px_40px_-24px_rgba(15,118,110,0.22)]">
            <img alt="Bimcubes" className="h-9 w-auto shrink-0 object-contain" data-testid="marketing-header-logo-image" src={logoUrl} />
          </div>
          <div>
            <div className="bim-display text-[1.2rem] font-semibold tracking-tight text-slate-950" data-testid="marketing-header-brand-name">
              Bimcubes
            </div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500" data-testid="marketing-header-brand-tagline">
              BIM & logiciels métiers
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="marketing-header-desktop-nav">
          {navItems.map((item) => (
            <NavItem item={item} key={item.label} />
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <Button asChild className="h-11 rounded-full bg-[#0f766e] px-6 text-[15px] text-white shadow-[0_20px_45px_-24px_rgba(15,118,110,0.45)] hover:bg-[#149289]">
            <a data-testid="marketing-header-estimate-button" href="/bim-3d-synthese#calculateur">
              Estimer un projet
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
          <div className="ml-1 flex items-center gap-3 border-l border-[#d8ece4] pl-4 text-[12px] text-slate-400" data-testid="marketing-header-internal-access">
            {internalAccessItems.map((item) => (
              <Link
                className="transition hover:text-[#0f766e]"
                data-testid={item.testId}
                key={item.label}
                to={item.href}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <Sheet onOpenChange={setOpen} open={open}>
          <SheetTrigger asChild>
            <Button className="rounded-full border-[#d8ece4] text-[#0f766e] lg:hidden" data-testid="marketing-header-mobile-menu-button" size="icon" variant="outline">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent className="w-[22rem] border-l border-[#d8ece4] bg-[rgba(252,254,249,0.97)] backdrop-blur-xl" side="right">
            <div className="sr-only">
              <SheetTitle data-testid="marketing-header-mobile-menu-title">Menu principal Bimcubes</SheetTitle>
              <SheetDescription data-testid="marketing-header-mobile-menu-description">Navigation mobile vers les rubriques du site Bimcubes.</SheetDescription>
            </div>
            <div className="mt-10 space-y-6" data-testid="marketing-header-mobile-panel">
              <div className="space-y-2">
                <div className="bim-display text-lg font-semibold text-slate-950" data-testid="marketing-header-mobile-brand-name">
                  Bimcubes
                </div>
                <div className="text-sm text-slate-500" data-testid="marketing-header-mobile-brand-copy">
                  Bureau d’études BIM & éditeur de logiciels.
                </div>
              </div>
              <nav className="grid gap-3" data-testid="marketing-header-mobile-nav">
                {navItems.map((item) => (
                  <NavItem item={item} key={item.label} mobile onNavigate={() => setOpen(false)} />
                ))}
              </nav>
              <Button asChild className="w-full rounded-full bg-[#0f766e] text-white hover:bg-[#149289]">
                <a data-testid="marketing-header-mobile-estimate-button" href="/bim-3d-synthese#calculateur" onClick={() => setOpen(false)}>
                  Estimer un projet
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <div className="border-t border-[#d8ece4] pt-4 text-[12px] text-slate-400">
                <div className="mb-2 uppercase tracking-[0.2em]">Accès interne</div>
                <div className="flex items-center gap-4">
                  {internalAccessItems.map((item) => (
                    <Link
                      className="transition hover:text-[#0f766e]"
                      data-testid={`${item.testId}-mobile`}
                      key={item.label}
                      onClick={() => setOpen(false)}
                      to={item.href}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export const MarketingFooter = () => {
  return (
    <footer className="mt-16 border-t border-slate-200 bg-slate-950 text-slate-300" data-testid="marketing-footer">
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 py-12 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:px-8">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-2">
              <img alt="Bimcubes" className="h-8 w-auto shrink-0 object-contain" data-testid="marketing-footer-logo-image" src={logoUrl} />
            </div>
            <div>
              <div className="bim-display text-lg font-semibold text-white" data-testid="marketing-footer-brand-name">
                Bimcubes
              </div>
              <div className="text-xs uppercase tracking-[0.22em] text-slate-400" data-testid="marketing-footer-brand-tagline">
                Solutions BIM
              </div>
            </div>
          </div>
          <p className="max-w-xl text-sm leading-7 text-slate-400" data-testid="marketing-footer-description">
            Solutions BIM — modélisation, coordination, planning, logiciels et formation.
          </p>
        </div>

        <div className="grid gap-3 text-sm">
          <a className="transition hover:text-white" data-testid="marketing-footer-email-link" href="mailto:contact@bimcubes.fr">
            contact@bimcubes.fr
          </a>
          <Link className="transition hover:text-white" data-testid="marketing-footer-home-link" to="/">Accueil</Link>
          <Link className="transition hover:text-white" data-testid="marketing-footer-bim3d-link" to="/bim-3d-synthese">BIM 3D</Link>
          <Link className="transition hover:text-white" data-testid="marketing-footer-bim4d-link" to="/bim-4d-planning">BIM 4D</Link>
        </div>
      </div>
    </footer>
  );
};